//Third party components
import React from 'react';
import { PropTypes } from 'prop-types';
import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';

// Shared components
import { LineLoader, LoadingSpinner,PamLayoutGrid, Modal } from '@timmons-group/shared-react-components';

// Hooks
import { useGet, useAuth } from '@timmons-group/shared-react-components/hooks';
import { useLayout } from '../../hooks/useLayout';
import { hasPermission } from '../../helpers';
import { useNavigate } from 'react-router-dom';
import { ACLS } from '../../constants';

// Constants
const ModalMode = {
  View: 'VIEW',
};

const ModalAction = {
  View: 'VIEW',
  Close: 'CLOSE',
  Reset: 'RESET',
};

// Reducers
const initialModalState = {
  modalIsOpen: false,
  modalTitle: '',
  modalText: '',
  okLabel: 'OK',
  modalMode: null,
  rowData: null,
};

const modalReducer = (state, action) => {
  const rowData = { ...action?.payload?.rowData };
  switch (action.type) {
    case ModalAction.View:
      return { modalIsOpen: true, modalText: `NOT IMPLEMENTED - View ${rowData?.name}`, modalMode: ModalMode.View, rowData };
    default:
      throw new Error('This mode is not supported.');
  }
}

const ActivitiesGrid = ({pathSegment, layoutKey}) => {
  const [activities, activitiesLoading] = useGet(`api/activity/${pathSegment}`, []);
  const [layout, layoutLoading] = useLayout(`${layoutKey.replace('List', '')}`, `${layoutKey}`);
  const [modalState, modalDispatch] = React.useReducer(modalReducer, initialModalState);
  const { authState } = useAuth();

  // props.acl is a backdoor to allow storybook stories to pass in acls
  const acl = authState?.user?.acl;
  const nav = useNavigate();

  const handleViewClick = (row) => {
    nav(`/activities/${row.id}`);
  };

  let actionList = [];
  if (hasPermission(ACLS.SEE_ACTIVITIES, acl)) {
    actionList.push({
      label: 'VIEW',
      order: 0,
      clickHandler: handleViewClick,
    });
  }

  const actionData = [{
    order: 0,
    actionList,
    label: 'Actions',
  }];

  const handleModalAffirmative = () => {
    try {
      modalDispatch({ type: ModalAction.Close });
    } catch (error) {
      console.error(error);
    } finally {
      // Ensure that we clear the data
      modalDispatch({ type: ModalAction.Reset });
    }
  }

  const handleModalCancel = () => {
    modalDispatch({ type: ModalAction.Reset });
  }

  const renderActivities = () => {
    let message = 'Loading Activities...'

    if (activitiesLoading) {
      if (pathSegment === 'all') {
        message = 'Loading All Activities...';
      } else {
        message = 'Loading Planned Activities...';
      }
      return <LineLoader message={message} color="secondary" />;
    }

    if (!activities || !activities.length) {
      return <p>No Activities found</p>;
    }

    return activitiesLoading || layoutLoading ?
      (<Box>
        <LineLoader message={message} color="secondary" />
      </Box>)
      :
      (<Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mt={2}
        minHeight="500px"
        width="100%"
      >
        <PamLayoutGrid
          actions={actionData}
          data={activities}
          getRowId={(row) => row.id}
          initialSortColumn={(pathSegment == 'all') ? 'activityCategory' : 'plannedStartDate'}
          initialSortDirection={'asc'}
          layout={layout}
          autoHeight
          disableSelectionOnClick
          density="compact"
          getCellClassName={(params) => {
            if (pathSegment == 'planned' &&
            params.field == 'plannedStartDate' &&
            params.value &&
            params.value < new Date()) {
              return 'text-red';
            } else {
              return '';
            }
          }}
        />
      </Box>)
  };

  return (
    <>
      {renderActivities()}
      <Modal
        okLabel={modalState.okLabel || 'Confirm'}
        cancelLabel="Cancel"
        title={modalState.modalTitle}
        onOk={handleModalAffirmative}
        onCancel={handleModalCancel}
        handleClose={handleModalCancel}
        showX={true}
        open={modalState.modalIsOpen}
        hideActions={false}
        hideCancel={false}
      >
        <Typography gutterBottom>
          {modalState.modalText}
        </Typography>
      </Modal>
    </>
  );
}

ActivitiesGrid.propTypes = {
  pathSegment: PropTypes.string,
  layoutKey: PropTypes.string,
}

export default ActivitiesGrid;
